import { formatPartLabel } from './partLabel';

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// One id per thing a person does, shared by every save that thing sets off. A deposit
// on a thirty-line order writes thirty-one log rows; this is what lets the log show
// them as the one action they actually were.
export const newBatchId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

// Travels with every part save so the log the server writes can name the order, the
// action and the part, instead of recording a bare new number that nobody can trace
// back to a decision. The part's readable name is built here because the naming rules
// already live in the browser - the server stores whatever it is handed, so there is
// only ever one version of a part's name.
const stockContextFor = (quotationObject, reason, inventoryRecord, batchId) => ({
  context: {
    reason,
    batchId,
    quotationNumber: quotationObject.get('quotationNumber'),
    sourceClass: quotationObject.className,
    partLabel: formatPartLabel(inventoryRecord.attributes),
  },
});

// partID is not unique - the same code exists on several rows with different
// dimensions - so match on the saved objectId first and only fall back to partID
// for older quotation parts that were saved without one.
export const findInventoryRecord = (allPartsArray, part) => {
  const byObjectId = part?.id && allPartsArray.find((item) => item.id === part.id);
  return byObjectId || allPartsArray.find((item) => item.get('partID') === part?.partID);
};

// What was on the shelf before this order was taken out of it. Orders deposited
// after this field was introduced carry the exact figure; older ones fall back to
// the level captured when the part was quoted, then to whatever is live now.
export const getAvailableQuantity = (part) => {
  if (part?.availableAtDeposit !== undefined && part?.availableAtDeposit !== null) {
    return toNumber(part.availableAtDeposit);
  }
  if (part?.inventoryQuantity !== undefined && part?.inventoryQuantity !== null) {
    return toNumber(part.inventoryQuantity);
  }
  return toNumber(part?.liveInventoryQuantity);
};

// An ordered quantity splits into units the warehouse can pick and units that have
// to be bought in. Both picklists are driven from this split, so a part with exactly
// enough stock lands on the internal list instead of falling through both of them.
export const splitPickQuantities = (orderedQuantity, availableQuantity) => {
  const ordered = Math.max(0, toNumber(orderedQuantity));
  const available = Math.max(0, toNumber(availableQuantity));
  const fromStock = Math.min(ordered, available);
  return { fromStock, shortfall: ordered - fromStock };
};

// Every class the parts can live in has more rows than a Parse query returns by
// default, so an unlimited query silently hides parts from the stock deduction.
export const findAllParts = async (Parse) => {
  const queryFor = (className) => new Parse.Query(Parse.Object.extend(className)).limit(1000);
  const [cantileverParts, palletRackingParts, palletRackingUsedParts] = await Promise.all([
    queryFor('CantileverParts').find(),
    queryFor('PalletRackingParts').find(),
    queryFor('PalletRackingPartsUsed').find(),
  ]);
  return [...cantileverParts, ...palletRackingParts, ...palletRackingUsedParts];
};

// A revision's number is its parent's number plus a letter, and revisions always hang
// off the top parent rather than off each other - so an order's whole family is
// reachable from any one of its numbers.
export const baseOrderNumberOf = (quotationObject) =>
  (quotationObject?.get('quotationNumber') ?? '').split('-')[0];

// The parent order plus every revision of it. Matched on the number rather than
// through the parent's revisions relation, because older orders were saved without
// that relation being kept up to date and a family with a member missing from it is
// exactly the case that lets stock be deducted twice.
export const findOrderFamily = async (Parse, quotationObject) => {
  const baseNumber = baseOrderNumberOf(quotationObject);
  if (!baseNumber) {
    return [quotationObject];
  }

  const parentQuery = new Parse.Query(Parse.Object.extend('Quotations'));
  parentQuery.equalTo('quotationNumber', baseNumber);
  const revisionQuery = new Parse.Query(Parse.Object.extend('QuotationRevisions'));
  revisionQuery.startsWith('quotationNumber', `${baseNumber}-`);
  revisionQuery.limit(1000);

  const [parents, revisions] = await Promise.all([parentQuery.find(), revisionQuery.find()]);
  return [...parents, ...revisions];
};

// What an order is currently holding out of stock, so a person can be told what is
// about to go back before they commit to it.
export const heldStockSummary = (quotationObject) => {
  const parts = quotationObject.get('quotationParts') ?? [];
  return {
    units: parts.reduce((total, part) => total + toNumber(part?.quantity), 0),
    partCount: parts.length,
  };
};

// Takes the ordered quantity out of stock and records what was on the shelf
// beforehand, so the picklist can tell picked units from units still to be bought.
// Guarded so a second trip through deposit/confirm cannot deduct the same order twice.
export const deductOrderedStock = async (Parse, quotationObject, allPartsArray, reason = 'deposit', batchId) => {
  // Only one object in an order family may hold stock at a time. A revision that
  // supersedes its parent inherits the shelf - it does not take a second helping from
  // it. Without this, a parent deposited before its revision was finalised keeps its
  // units out of stock forever, because finalising hides the only dropdown that could
  // have given them back.
  await releaseSupersededStock(Parse, quotationObject, allPartsArray, batchId);

  if (quotationObject.get('stockDeductedAt')) {
    return false;
  }
  const quotationNumber = quotationObject.get('quotationNumber');
  const stampedParts = [...(quotationObject.get('quotationParts') ?? [])];

  for (let index = 0; index < stampedParts.length; index += 1) {
    const part = stampedParts[index];
    const partToUpdate = findInventoryRecord(allPartsArray, part);
    if (!partToUpdate) {
      console.error('No inventory record found for quotation part:', part);
      continue;
    }
    const partHoldStatus = partToUpdate.get('partHoldStatus') ?? {};
    stampedParts[index] = { ...part, availableAtDeposit: toNumber(partToUpdate.get('quantity')) };
    partToUpdate.set('quantity', toNumber(partToUpdate.get('quantity')) - toNumber(part.quantity));
    delete partHoldStatus[quotationNumber];
    partToUpdate.set('partHoldStatus', partHoldStatus);
    await partToUpdate.save(null, stockContextFor(quotationObject, reason, partToUpdate, batchId));
  }

  quotationObject.set('quotationParts', stampedParts);
  quotationObject.set('stockDeductedAt', new Date());
  await quotationObject.save(null, { context: { batchId } });
  return true;
};

// Puts a released order's stock back and clears the guard so it can be deposited
// again. Guarded on the same flag as the deduction, so an order that never reached
// deposit - or one that was already restored - cannot inflate stock. The shelf
// snapshot goes with it: it only describes an order that is currently out of stock.
export const restoreOrderedStock = async (quotationObject, allPartsArray, reason = 'release', batchId) => {
  if (!quotationObject.get('stockDeductedAt')) {
    return false;
  }
  const quotationNumber = quotationObject.get('quotationNumber');
  const stampedParts = [...(quotationObject.get('quotationParts') ?? [])];

  for (let index = 0; index < stampedParts.length; index += 1) {
    const part = stampedParts[index];
    const partToUpdate = findInventoryRecord(allPartsArray, part);
    if (!partToUpdate) {
      console.error('No inventory record found for quotation part:', part);
      continue;
    }
    const partHoldStatus = partToUpdate.get('partHoldStatus') ?? {};
    const { availableAtDeposit, ...partWithoutSnapshot } = part;
    stampedParts[index] = partWithoutSnapshot;
    partToUpdate.set('quantity', toNumber(partToUpdate.get('quantity')) + toNumber(part.quantity));
    delete partHoldStatus[quotationNumber];
    partToUpdate.set('partHoldStatus', partHoldStatus);
    await partToUpdate.save(null, stockContextFor(quotationObject, reason, partToUpdate, batchId));
  }

  quotationObject.set('quotationParts', stampedParts);
  quotationObject.unset('stockDeductedAt');
  await quotationObject.save(null, { context: { batchId } });
  return true;
};

// Hands the shelf over to one member of an order family: every other member that is
// still holding stock gives it back, and drops to open so its status and its stock
// agree. Returns what was released, so the caller can say so.
//
// This is the whole of the double-deduction fix. A quotation and its revisions are one
// physical order but separate Parse objects, each with its own copy of the parts and
// its own stockDeductedAt, so nothing stopped two of them being deducted for the same
// units.
export const releaseSupersededStock = async (Parse, keeper, allPartsArray, batchId) => {
  const family = await findOrderFamily(Parse, keeper);
  const released = [];

  for (const member of family) {
    if (member.id === keeper.id || !member.get('stockDeductedAt')) {
      continue;
    }

    const { units, partCount } = heldStockSummary(member);
    await restoreOrderedStock(member, allPartsArray, 'superseded-release', batchId);

    // Its stock is back on the shelf, so it can no longer read as deposited.
    member.set('status', 'open');
    member.set('quotationStatus', 'open');
    await member.save(null, { context: { batchId } });

    released.push({ quotationNumber: member.get('quotationNumber'), units, partCount });
  }

  return released;
};

// Drops this quotation's holds without touching stock, for an order that reserved
// parts but never got as far as deposit. Nothing was deducted, so nothing goes back.
export const clearPartHolds = async (quotationObject, allPartsArray, reason = 'hold-release', batchId) => {
  const quotationNumber = quotationObject.get('quotationNumber');

  for (const part of quotationObject.get('quotationParts') ?? []) {
    const partToUpdate = findInventoryRecord(allPartsArray, part);
    if (!partToUpdate) {
      continue;
    }
    const partHoldStatus = partToUpdate.get('partHoldStatus') ?? {};
    if (!(quotationNumber in partHoldStatus)) {
      continue;
    }
    delete partHoldStatus[quotationNumber];
    partToUpdate.set('partHoldStatus', partHoldStatus);
    await partToUpdate.save(null, stockContextFor(quotationObject, reason, partToUpdate, batchId));
  }
};
